import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { find, get, isEmpty } from 'lodash-es';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import { buildTransactionCountPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { CalculatorsModel, getStoredSelectionsPath } from 'client/data/models/calculators';
/* Components */
import { OfferTypeTablePriceChart } from 'site-modules/shared/components/car-buying/offer-type-table/offer-type-table-price-chart';
import { PriceCheckerEdmundsAdvice } from 'site-modules/shared/components/price-checker-edmunds-advice/price-checker-edmunds-advice';
import { PriceCheckerIncentives } from 'site-modules/shared/components/price-checker/price-checker-incentives/price-checker-incentives';

/* Utils */
import {
  getTmvData,
  getTransactionCountCopyValues,
  getVDPPriceData,
} from 'client/site-modules/shared/utils/price-checker';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getIsNonTMVDealer } from 'site-modules/shared/utils/inventory/vdp-utils';
import { getTotalRebates } from 'site-modules/shared/components/inventory/utils/get-total-rebates';
import { getSellingPrice } from 'site-modules/shared/components/car-buying/offer-type-table/offer-type-table-chart-utils';
import { getPrices } from 'site-modules/shared/utils/inventory-utils/get-prices';
import { getEdmundsSuggestedPrice } from 'site-modules/shared/utils/inventory-utils/prices';
import { getLoanPrimaryIncentives } from 'site-modules/shared/utils/incentives/incentives-service';
/* Constants */
import { LEASE, PURCHASE } from 'site-modules/shared/constants/car-buying';
import { ADVERTISED_PRICE_SOURCE } from 'site-modules/shared/constants/inventory/prices';

import './vdp-pricing-chart.scss';

function getSellingPriceForPaymentType({ showLeasePayments, dealItems, isPurchase }) {
  const items = dealItems[isPurchase ? PURCHASE : LEASE];
  if (showLeasePayments) {
    return items?.[0]?.calculatedData?.msrpBaseMonthlyPayment ?? 0;
  }
  return getSellingPrice({ dealItems: items, isPurchase });
}

function getDealerPrice({ vinData, dealItems, showLeasePayments }) {
  const { prices, computedDisplayInfo } = vinData;
  const { advertisedPrice, advertisedPriceSource } = getPrices(prices, computedDisplayInfo);
  if (showLeasePayments) {
    return dealItems[LEASE]?.[0]?.calculatedData?.msrpBaseMonthlyPayment ?? 0;
  }
  return advertisedPriceSource === ADVERTISED_PRICE_SOURCE.INTERNET_PRICE ? advertisedPrice : 0;
}

export class VdpPricingChartUI extends Component {
  static propTypes = {
    vinData: PropTypes.oneOfType([
      InventoryEntities.InventoryVin,
      PropTypes.shape({
        prices: PropTypes.shape({
          displayPrice: PropTypes.number,
        }),
      }),
    ]).isRequired,
    dealItems: PropTypes.shape({ [PropTypes.string]: PropTypes.arrayOf(PropTypes.shape({})) }),
    creativeId: PropTypes.string,
    isPurchase: PropTypes.bool,
    isMobile: PropTypes.bool,
    skipWidgetViewTracking: PropTypes.bool,
    isUsed: PropTypes.bool,
    showLeasePayments: PropTypes.bool,
    isLeasePaymentEnabled: PropTypes.bool,
    transactionCount: VehicleEntities.StyleTransactionCount,
    isLeaseView: PropTypes.bool,
    forceLease: PropTypes.bool,
    storedLeaseSelections: PropTypes.shape({
      numberOfMonths: PropTypes.number,
    }),
  };

  static defaultProps = {
    dealItems: {},
    isPurchase: false,
    isMobile: false,
    skipWidgetViewTracking: false,
    isUsed: false,
    creativeId: null,
    showLeasePayments: false,
    isLeasePaymentEnabled: false,
    transactionCount: null,
    isLeaseView: false,
    forceLease: false,
    storedLeaseSelections: {},
  };

  static getDerivedStateFromProps(
    { vinData, showLeasePayments, dealItems, isPurchase },
    {
      dealItems: prevDealItems,
      vinData: prevVinData,
      isPurchase: prevIsPurchase,
      showLeasePayments: prevShowLeasePayments,
    }
  ) {
    if (
      dealItems === prevDealItems &&
      vinData === prevVinData &&
      isPurchase === prevIsPurchase &&
      showLeasePayments === prevShowLeasePayments
    ) {
      return null;
    }

    if (isEmpty(isPurchase ? dealItems[PURCHASE] : dealItems[LEASE])) {
      return {
        dealItems,
        vinData,
        isPurchase,
        showLeasePayments,
      };
    }

    const tmvData = getTmvData({
      dealItems,
      isPurchase,
      showLeasePayments,
    });
    const sellingPrice = getSellingPriceForPaymentType({
      showLeasePayments,
      dealItems,
      isPurchase,
    });
    const dealType = getVDPPriceData({
      ...tmvData,
      userDealerPrice: sellingPrice,
    }).dealType.toLowerCase();
    const apiDealType = get(vinData, 'computedDisplayInfo.priceValidation.dealType', '').toLowerCase();

    const targetDealType = dealType || apiDealType;

    return {
      selectedDealType: targetDealType === 'above_market' ? 'high' : targetDealType,
      inputValue: sellingPrice,
      dealItems,
      vinData,
      isPurchase,
      showLeasePayments,
    };
  }

  constructor(props) {
    super(props);

    const { vinData, isPurchase, dealItems, showLeasePayments } = this.props;

    const selectedDealType = get(vinData, 'computedDisplayInfo.priceValidation.dealType', '').toLowerCase();

    this.state = {
      selectedDealType: selectedDealType === 'above_market' ? 'high' : selectedDealType, // should display high price display for above_market price
      inputValue: 0,
      showAdvice: false,
      dealItems,
      vinData,
      isPurchase,
      showLeasePayments,
    };
  }

  getPurchasePrice() {
    const { vinData, isUsed } = this.props;
    const tmvPrice = get(vinData, 'prices.tmv', 0);
    const displayPrice = get(vinData, 'prices.advertisedPrice', 0);

    if (!this.isTMVDealer()) {
      return displayPrice;
    }

    if (!isUsed && tmvPrice) {
      return tmvPrice;
    }

    return displayPrice;
  }

  getBestDealPrice() {
    const { vinData, isUsed, showLeasePayments } = this.props;
    const selectedIncentives = this.getSelectedDealIncentives();
    const totalRebate = getTotalRebates({ incentives: selectedIncentives, showLeasePayments });

    if (isUsed) {
      return get(vinData, 'computedDisplayInfo.priceValidation.listPriceEstimate', 0);
    }

    const edmundsSuggestedPrice = getEdmundsSuggestedPrice({
      vinData,
      selectedIncentives,
      primaryLoanIncentives: getLoanPrimaryIncentives({ vinData }),
      showLeasePayments,
    });

    const isCompliant = get(vinData, 'prices.edmundsSuggestedPriceIsCompliant', false);
    if (isCompliant) return edmundsSuggestedPrice;

    const purchasePrice = this.getPurchasePrice();

    return purchasePrice && (totalRebate || this.isTMVDealer()) ? purchasePrice - totalRebate : 0;
  }

  getSelectedDealIncentives() {
    const { dealItems, showLeasePayments } = this.props;
    const { selectedDealType } = this.state;

    const deal = dealItems?.[showLeasePayments ? LEASE : PURCHASE]?.find(
      dealItem => dealItem.dealOffer?.dealType === selectedDealType
    );

    return deal?.dealOffer?.incentives ?? [];
  }

  getSelectedDealRebatesAmount() {
    return getTotalRebates({
      incentives: this.getSelectedDealIncentives(),
      showLeasePayments: this.props.showLeasePayments,
    });
  }

  getBestLeasePayment() {
    const monthlyBestDealPrice = get(
      find(this.props.dealItems[LEASE], deal => get(deal, 'dealOffer.dealType') === 'good'),
      'dealOffer.baseMonthlyPayment'
    );

    return monthlyBestDealPrice ?? 0;
  }

  isTMVDealer() {
    const { vinData } = this.props;

    const franchiseId = get(vinData, 'dealerInfo.franchiseId', '');
    return !getIsNonTMVDealer(franchiseId);
  }

  /**
   * Handles deal type hover when slider moves.
   * @param {object} data
   */
  handleSliderMoveHoverRaise = data => {
    const dealType = get(data, 'dealType', '');
    const inputValue = get(data, 'inputValue', '');

    this.setState({ inputValue, selectedDealType: dealType, showAdvice: true });
  };

  render() {
    const {
      vinData,
      dealItems,
      isPurchase,
      isMobile,
      skipWidgetViewTracking,
      isUsed,
      creativeId,
      showLeasePayments,
      isLeasePaymentEnabled,
      transactionCount,
      isLeaseView,
      forceLease,
      storedLeaseSelections,
    } = this.props;
    const { selectedDealType, inputValue, showAdvice } = this.state;
    const stateCode = get(vinData, 'dealerInfo.address.stateCode', '');
    const dealerPrice = getDealerPrice({ vinData, dealItems, showLeasePayments });

    const dealerState = get(vinData, 'dealerInfo.address.stateName', '');
    const transactionCountCopyValues = transactionCount
      ? getTransactionCountCopyValues({
          transactionCount,
          styleParams: {
            trim: get(vinData, 'vehicleInfo.styleInfo.trim', ''),
            makeName: get(vinData, 'vehicleInfo.styleInfo.make', ''),
            modelName: get(vinData, 'vehicleInfo.styleInfo.model', ''),
          },
          dealerState,
          isVdpPriceChecker: true,
        })
      : undefined;
    const currentTransactionCount = transactionCount ? get(transactionCount, 'transactionCount') : undefined;
    const tmvData = getTmvData({
      dealItems,
      isPurchase,
      showLeasePayments,
    });
    const totalRebate = this.getSelectedDealRebatesAmount();

    const sellingPrice = getSellingPriceForPaymentType({
      showLeasePayments,
      dealItems,
      isPurchase,
    });

    return (
      <div className="vdp-pricing-chart">
        <OfferTypeTablePriceChart
          onSliderMove={this.handleSliderMoveHoverRaise}
          dealItems={dealItems}
          tmvData={tmvData}
          selectedDealType={selectedDealType}
          isMobile={isMobile}
          isPurchase={isPurchase}
          isUsed={isUsed}
          skipWidgetViewTracking={skipWidgetViewTracking}
          creativeId={creativeId}
          dealerPrice={dealerPrice}
          sellingPrice={sellingPrice}
          showLeasePayments={showLeasePayments}
          noDealCheckAnimation
          isLeasePaymentEnabled={isLeasePaymentEnabled}
          transactionCount={currentTransactionCount}
          forceLease={forceLease}
        />
        <PriceCheckerEdmundsAdvice
          inputValue={Number(inputValue)}
          bestDealPrice={showLeasePayments ? this.getBestLeasePayment() : this.getBestDealPrice()}
          dealType={selectedDealType}
          dealerPrice={dealerPrice}
          showAdvice={showAdvice}
          isUsed={isUsed}
          stateCode={stateCode}
          showLeasePayments={showLeasePayments}
          transactionCountCopyValues={transactionCountCopyValues}
          forceLease={forceLease}
          totalRebate={totalRebate}
        />
        {!isUsed && (
          <PriceCheckerIncentives
            vinData={vinData}
            isLeaseView={isLeaseView}
            selectedLeaseTerm={storedLeaseSelections.numberOfMonths}
          />
        )}
      </div>
    );
  }
}

export const stateToPropsConfig = {
  transactionCount: bindToPath(({ vinData }) => {
    const styleId = get(vinData, 'vehicleInfo.styleInfo.styleId', '');
    return isNew(vinData.type) && !!styleId && buildTransactionCountPath(styleId);
  }, VehicleModel),
  storedLeaseSelections: bindToPath(getStoredSelectionsPath({ isPurchase: false }), CalculatorsModel, null, false),
};

export const VdpPricingChart = connectToModel(VdpPricingChartUI, stateToPropsConfig);
